<template>
  <div class="form-container" id="print">
    <div class="wrapper">
      <fields :detail="detail" />
    </div>
    <b-table
      thead-class="bg-color text-center"
      empty-text="No data"
      :items="detail.equipment_use"
      :fields="visibleFields"
      responsive
      show-empty
    >
      <template v-slot:table-busy>
        <div class="text-center my-4">
          <b-spinner
            variant="info"
            class="spinner-lg"
          />
        </div>
      </template>
      <template #cell(number_of_unit)="items">
        {{ items.item.number_of_unit ? items.item.number_of_unit.toString() : '-' }}
      </template>
      <template #cell(est_time_perday)="items">
        {{ items.item.est_time_perday ? items.item.est_time_perday.toString() : '-' }}
      </template>
      <template #cell(est_day)="items">
        {{ items.item.est_day ? items.item.est_day.toString() : '-' }}
      </template>
      <template #cell(total_est_time)="items">
        {{ items.item.total_est_time ? items.item['total_est_time'].toLocaleString('en-US')  : '-' }}
      </template>
      <template #cell(est_cost)="items">
        {{ items.item.est_cost ? items.item['est_cost'].toLocaleString('en-US') : '-' }}
      </template>
      <template #cell(actual_wt)="items">
        {{ items.item.actual_wt ? items.item.actual_wt.toString() : '-' }}
      </template>
      <template #cell(progress)="items">
        {{ items.item.progress_usd ? items.item.progress_usd : '-' }} / {{ items.item.progress_percentage ? `${items.item.progress_percentage} %` : '0 %' }}
      </template>
      <template v-slot:custom-foot>
        <tr class="bg-color text-center">
          <td></td>
          <td></td>
          <td></td>
          <td class="font-weight-bold">Total</td>
          <td>{{ subTotal('total_est_time') }}</td>
          <td v-if="showCost">{{ subTotal('est_cost').toLocaleString('en-US') }}</td>
          <td v-if="showCost"></td>
          <td v-if="showCost"></td>
        </tr>
      </template>
    </b-table>
    <div class="d-flex mt-5">
      <b-button
        class="btn-reset mr-2"
        variant="outline-info"
        size="sm"
        @click="onClickBack"
      >
        Back
      </b-button>
      <div id="history">
        <b-button
          ref="button"
          class="btn-custom mr-2"
          variant="info"
          size="sm"
          @click="isHidden = !isHidden"
        >
         Log History
        </b-button>
        <b-card
          class="log-history text-center"
          v-show="isHidden"
          v-closable="{
            exclude: ['button'],
            handler: 'onClose'
          }"
        >
          <div class="timeline small">
            <div v-if="detail.approval_history && detail.approval_history.length > 0">
              <ul class="list-history">
                <li
                  class="list-history-child"
                  v-for="(info, index) in detail.approval_history"
                  v-bind:key="index">
                  <div class="timeline-log timeline-name">
                    {{ info.fullname }} - {{ info.event }}
                  </div>
                  <div class="timeline-log timeline-status">
                    {{ info.state }}
                  </div>
                  <div class="timeline-log">
                    {{ info.comment }}
                  </div>
                  <div class="timeline-log timeline-date mb-3">
                    {{ `${formatDate(info.created_at)}` }}
                  </div>
                </li>
              </ul>
            </div>
            <div class="no-history" v-else>
              <h5>There is no data history in here.</h5>
            </div>
          </div>
        </b-card>
      </div>
      <b-button
        class="btn-custom mr-2"
        variant="info"
        size="sm"
        @click="onPrint"
      >
        Print
      </b-button>
      <b-button
        v-if="canEdit && detail.approval_status.isEdit"
        class="btn-custom mr-auto"
        variant="info"
        size="sm"
        @click="onEdit"
      >
        Edit
      </b-button>
      <b-button
        v-if="detail.approval_status.isActiveRevise"
        class="btn-revise-cstm mr-2 ml-auto"
        variant="danger"
        size="sm"
        @click="onRevise"
        :disabled="isLoading"
      >
        Revise
      </b-button>
      <b-button
        v-if="detail.approval_status.isActiveApprove"
        :class="classBtnApprove()"
        variant="info"
        size="sm"
        @click="onApprove"
        :disabled="isLoading"
      >
        Approve
      </b-button>
    </div>
    <modal-approve @submit="showComment" :form="detail" :text="text" />
    <modal-comment @submit="updateStatus" :action="action" />
    <modal-successful :message="text" />
  </div>
</template>

<script>
import Fields from './Field'
import api from '@/api'
import ModalApprove from '@/partials/ModalApprove'
import ModalComment from '@/partials/ModalComment'
import ModalSuccessful from '@/partials/ModalSuccessful'
import { hasPermission } from '@/utils/permissions'
import { WORK_ORDER_EDIT } from '@/fixtures'
import moment from 'moment'
import { closeEvent } from '@/utils/closeEvent'

closeEvent()

export default {
  components: {
    Fields,
    ModalApprove,
    ModalComment,
    ModalSuccessful
  },

  data: () => ({
    profile: JSON.parse(localStorage.getItem('profile')),
    canEdit: hasPermission(WORK_ORDER_EDIT),
    fields: [
      { key: 'equipment', label: 'Unit Type', tdClass: 'text-center' },
      { key: 'number_of_unit', label: 'Number of Unit', tdClass: 'text-center' },
      { key: 'est_time_perday', label: 'Est Time/Day', tdClass: 'text-center' },
      { key: 'est_day', label: 'Est Day', tdClass: 'text-center' },
      { key: 'total_est_time', label: 'Total Est Time', tdClass: 'text-center' },
      { key: 'est_cost', label: 'Est Cost', tdClass: 'text-center', visible: true },
      { key: 'actual_wt', label: 'Actual Working Time', tdClass: 'text-center', visible: true },
      { key: 'progress', label: 'Progress ($/%)', tdClass: 'text-center', visible: true }
    ],
    detail: {
      approval_status: {
        isEdit: false
      },
      contractor_name: '',
      validity_periode_start: '',
      validity_periode_end: '',
      contractor_number: '',
      department: '',
      order_number: '',
      job_desc: '',
      budget_code: '',
      status: '',
      equipment_use: [],
      company_id: 0,
      division: {
        name: ''
      }
    },
    text: '',
    action: '',
    comment: '',
    isHidden: false,
    isLoading: false,
    showCost: true
  }),

  created() {
    this.fetctDetailWorkOrder()
  },

  computed: {
    visibleFields() {
      if (this.profile.contractors) {
        return this.fields.filter(field => !field.visible)
      } else {
        return this.fields
      }
    }
  },

  methods: {
    async fetctDetailWorkOrder() {
      const data = await api.workOrder.get(this.$route.params.id)
      this.detail = data
      this.detail.division = this.detail.division ?? { name: '' }
      this.detail.validity_periode_start = this.detail.validity_periode_start ? moment(this.detail.validity_periode_start).format('DD MMM YYYY') : null
      this.detail.validity_periode_end = this.detail.validity_periode_end ? moment(this.detail.validity_periode_end).format('DD MMM YYYY') : null

      if (this.profile.contractors) {
        this.showCost = false
      }
    },
    onClickBack() {
      this.$router.push({ path: '/work-order' })
    },
    onPrint() {
      let url = ''
      if (this.profile.contractors) {
        url = `${process.env.VUE_APP_PRINT_URL}/ReportWorkOrderContractor?WoId=${this.$route.params.id}&CompanyId=${this.detail.company_id}`
      } else {
        url = `${process.env.VUE_APP_PRINT_URL}/ReportWorkOrder?WoId=${this.$route.params.id}&CompanyId=${this.detail.company_id}`
      }
      window.open(url)
    },
    onEdit() {
      this.$router.push({ path: `/work-order/${this.$route.params.id}/edit` })
    },
    onApprove() {
      this.text = 'Approve this work order'
      this.action = 'approve'
      this.$bvModal.show('modal-approve')
    },
    onRevise() {
      this.text = 'Revise this work order'
      this.action = 'revise'
      this.$bvModal.show('modal-approve')
    },
    onClose() {
      this.isHidden = false
    },
    showComment() {
      this.$bvModal.show('modal-comment')
    },
    async updateStatus(comment) {
      try {
        this.isLoading = true
        const checkStatus = this.action === 'approve'
          ? await api.workOrder.approve(this.$route.params.id, { comment: comment })
          : await api.workOrder.revise(this.$route.params.id, { comment: comment })
        if (checkStatus.data.error) {
          this.isLoading = false
          this.$nextTick(() => {
            this.$bvToast.toast(`${checkStatus.data.error.message}`, {
              headerClass: 'd-none',
              solid: true,
              variant: 'danger'
            })
          })
        } else {
          this.text = this.action === 'approve' ? 'Approve this work order' : 'Revise this work order'
          this.$bvModal.show('modal-successful')
          setTimeout(() => {
            this.$router.push('/work-order')
          }, 1500)
        }
      } catch ({ response }) {
        this.isLoading = false
        this.$nextTick(() => {
          this.$bvToast.toast(`${response.status} ${response.data.error.message}`, {
            headerClass: 'd-none',
            solid: true,
            variant: 'danger'
          })
        })
      }
    },
    subTotal(key) {
      let x = 0
      this.detail.equipment_use.forEach(d => {
        x += d[key]
      })

      return x
    },
    formatDate(date) {
      return moment(date).format('DD-MM-YYYY hh:mm:ss')
    },
    classBtnApprove() {
      return this.detail.approval_status.isActiveRevise ? 'btn-custom' : 'btn-custom ml-auto'
    }
  },

  destroyed() {
    localStorage.removeItem('currentPage', this.currentPage)
    localStorage.removeItem('totalRows', this.totalRows)
  }
}
</script>

<style lang="scss">
.form-control {
  font-size: 12px;
}

.col-form-label, .col-form-label-sm {
  font-size: 12px !important;
}
</style>
